// CAPABILITY: Viewer session lifecycle for public /s/:slug pages.
// INPUTS: pageId, enabled (false in preview), isPlaying (from VideoPlayer), stage ("gate" = password/countdown,
//         "player" = video visible), playbackSignal (how play/pause is observed; see VideoPlayer getPlaybackSignal).
// OUTPUTS: SESSION_START → HEARTBEAT every 30s (+ immediately on first play and on play/pause changes, throttled)
//          → HEARTBEAT beacon when the tab is hidden → SESSION_END beacon on unload/unmount.
// WATCH TIME is measured here (wall-clock ms while playing) and sent as watchSeconds deltas, so heartbeat
// frequency no longer limits accuracy. 30s cadence = 6× fewer paid writes than the old 5s cadence (ADR-003).
import { useEffect, useRef } from "react";
import {
  sendHeartbeat as sendHeartbeatEvent,
  sendSessionEnd,
  sendSessionStart,
} from "../api/viewerAnalytics";
import { createAnalyticsSessionId, createOrGetAnalyticsViewerId } from "../utils/sessionId";

const HEARTBEAT_INTERVAL_MS = 30000;
const MIN_STATE_CHANGE_GAP_MS = 5000;

export function useViewerAnalytics({ pageId, enabled, isPlaying, stage = "player", playbackSignal }) {
  const sessionIdRef = useRef(null);
  const viewerIdRef = useRef(null);
  const heartbeatSeqRef = useRef(0);
  const hasStartedRef = useRef(false);
  const hasEndedRef = useRef(false);
  const hasSentPlayingRef = useRef(false);
  const lastSentAtRef = useRef(0);
  const isPlayingRef = useRef(Boolean(isPlaying));
  const playingSinceRef = useRef(isPlaying ? Date.now() : null);
  const pendingWatchMsRef = useRef(0);
  const stageRef = useRef(stage);
  const signalRef = useRef(playbackSignal);
  const sendNowRef = useRef(() => {});

  stageRef.current = stage;
  signalRef.current = playbackSignal;

  // Drain measured play time since the last send (keeps the clock running if still playing).
  const takeWatchSeconds = () => {
    const now = Date.now();
    let ms = pendingWatchMsRef.current;
    if (playingSinceRef.current != null) {
      ms += now - playingSinceRef.current;
      playingSinceRef.current = now;
    }
    pendingWatchMsRef.current = 0;
    return Math.round(Math.max(0, ms) / 100) / 10;
  };

  useEffect(() => {
    if (!enabled || !pageId) return undefined;

    const sessionId = createAnalyticsSessionId();
    const viewerId = createOrGetAnalyticsViewerId();
    sessionIdRef.current = sessionId;
    viewerIdRef.current = viewerId;
    heartbeatSeqRef.current = 0;
    hasStartedRef.current = true;
    hasEndedRef.current = false;
    hasSentPlayingRef.current = false;
    pendingWatchMsRef.current = 0;
    playingSinceRef.current = isPlayingRef.current ? Date.now() : null;

    const sendNow = ({ useBeacon = false } = {}) => {
      if (!hasStartedRef.current || hasEndedRef.current) return;
      heartbeatSeqRef.current += 1;
      const watchSeconds = takeWatchSeconds();
      const playing = isPlayingRef.current || watchSeconds > 0;
      if (playing) hasSentPlayingRef.current = true;
      lastSentAtRef.current = Date.now();
      sendHeartbeatEvent(
        {
          sessionId,
          viewerId,
          pageId,
          heartbeatSeq: heartbeatSeqRef.current,
          isPlaying: playing,
          watchSeconds,
          stage: stageRef.current,
          signal: signalRef.current,
        },
        { useBeacon }
      );
    };
    sendNowRef.current = sendNow;

    sendSessionStart({ sessionId, viewerId, pageId, stage: stageRef.current, signal: signalRef.current });
    sendNow();

    const intervalId = window.setInterval(() => sendNow(), HEARTBEAT_INTERVAL_MS);

    const endSession = () => {
      if (hasEndedRef.current) return;
      const watchSeconds = takeWatchSeconds();
      hasEndedRef.current = true;
      sendSessionEnd({
        sessionId,
        viewerId,
        pageId,
        isPlaying: isPlayingRef.current || watchSeconds > 0,
        watchSeconds,
        stage: stageRef.current,
        signal: signalRef.current,
      });
    };
    // Hidden tabs get throttled timers (≈1/min) and may never unload cleanly on mobile: flush now.
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") sendNow({ useBeacon: true });
    };

    window.addEventListener("pagehide", endSession);
    window.addEventListener("beforeunload", endSession);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pagehide", endSession);
      window.removeEventListener("beforeunload", endSession);
      document.removeEventListener("visibilitychange", handleVisibility);
      endSession();
      hasStartedRef.current = false;
      sendNowRef.current = () => {};
    };
    // takeWatchSeconds only touches refs; session identity is keyed on page + enabled.
  }, [enabled, pageId]);

  // Play/pause transitions: bank play time, then report promptly (first play always immediate).
  useEffect(() => {
    const next = Boolean(isPlaying);
    if (next === isPlayingRef.current) return;
    const now = Date.now();
    if (next) {
      playingSinceRef.current = now;
    } else if (playingSinceRef.current != null) {
      pendingWatchMsRef.current += now - playingSinceRef.current;
      playingSinceRef.current = null;
    }
    isPlayingRef.current = next;
    if (!hasStartedRef.current || hasEndedRef.current) return;
    const isFirstPlay = next && !hasSentPlayingRef.current;
    if (isFirstPlay || now - lastSentAtRef.current >= MIN_STATE_CHANGE_GAP_MS) sendNowRef.current();
  }, [isPlaying]);

  // Passing the password/countdown gate is reported promptly so "reached player" is accurate.
  const previousStageRef = useRef(stage);
  useEffect(() => {
    if (previousStageRef.current === stage) return;
    previousStageRef.current = stage;
    if (stage === "player" && hasStartedRef.current && !hasEndedRef.current) sendNowRef.current();
  }, [stage]);
}

export { HEARTBEAT_INTERVAL_MS, MIN_STATE_CHANGE_GAP_MS };
